/** Small UI-only signals and view constants that don't belong in the domain store. */
import { signal } from '@preact/signals'
import type { Violation } from '../domain/validate'
import type { StintStroke } from '../analytics/stats'

/** Violations from the last export attempt; non-empty shows the failure panel. */
export const exportViolations = signal<readonly Violation[]>([])

/** The split-count chosen in the edit island. */
export const splitParts = signal(2)

/** Which metric the chart bar height encodes. Pace is the default. */
export type ChartMetric = 'duration' | 'pace' | 'strokes'
export const chartMetric = signal<ChartMetric>('pace')

/** The length ordinal currently hovered in the chart (for the tooltip), or null. */
export const hoveredOrdinal = signal<number | null>(null)

export const STROKES: readonly { value: number; label: string }[] = [
  { value: 0, label: 'Freestyle' },
  { value: 1, label: 'Backstroke' },
  { value: 2, label: 'Breaststroke' },
  { value: 3, label: 'Butterfly' },
  { value: 4, label: 'Drill' },
  { value: 5, label: 'Mixed' },
  { value: 6, label: 'IM' },
  { value: 255, label: 'Not recorded' },
]

export function strokeLabel(value: number): string {
  return STROKES.find((s) => s.value === value)?.label ?? 'Not recorded'
}

/** Colours from the mockup. "Not recorded" is dark gray so it reads apart from the lighter rests. */
export const REST_COLOR = '#d8d8dd'
export const SELECTED_COLOR = '#ff2d55'
export const UNKNOWN_STROKE_COLOR = '#48484a'

const STROKE_COLORS: Record<number, string> = {
  0: '#007aff', // Freestyle
  1: '#30b0c7', // Backstroke
  2: '#ff9500', // Breaststroke
  3: '#af52de', // Butterfly
  4: '#8e8e93', // Drill
  5: '#34c759', // Mixed
  6: '#ffcc00', // IM
  255: UNKNOWN_STROKE_COLOR, // Not recorded
}

/** Fill for an active length's bar / dot by its swim_stroke value. */
export function strokeColor(value: number): string {
  return STROKE_COLORS[value] ?? UNKNOWN_STROKE_COLOR
}

/** Colour + label for a set's stroke (a value, or 'mixed'). */
export function setStrokeColor(stroke: StintStroke): string {
  return stroke === 'mixed' ? '#8e8e93' : strokeColor(stroke)
}
export function setStrokeLabel(stroke: StintStroke): string {
  return stroke === 'mixed' ? 'Mixed' : strokeLabel(stroke)
}
